import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import UserContext from '../common/UserContext.js';

// react router
import {useHistory} from 'react-router-dom';

// components
import {HostPanelBarHeader} from './HostPanelbarHeader.js';
import {HostDetailPopover} from './HostPopovers.js';
import {EmailDialog} from '../common/EmailModal.js';
import Alert from '../common/Alert.js';
import {CurrencyCell, DateCell, NoWrapCell, TextAlignMiddleCell, TextCapitalizeCell} from '../common/Grid.js';
import {DownloadIcon, IconButton, MailIcon} from '../common/icons.js';
import {capitalizeSentence, downloadLicenses, formatCurrency} from '../common/utilities.js';

// kendo
import {PanelBar, PanelBarItem} from '@progress/kendo-react-layout';
import {Grid, GridColumn} from '@progress/kendo-react-grid';
import {Button} from '@progress/kendo-react-buttons';
import {Tooltip} from '@progress/kendo-react-tooltip';
import {fileExcelIcon} from '@progress/kendo-svg-icons';
import {orderBy} from '@progress/kendo-data-query';

// multilingual
import {useLocalization} from '@progress/kendo-react-intl';
import {
    downloadSelectedLicensesKey,
    emailSelectedLicensesKey,
    exportToExcelKey,
    costPerUnitKey,
    descriptionKey,
    endDateKey,
    licenseTypeKey,
    productNumberKey,
    qtyKey,
    startDateKey,
    totalCostKey,
    noLicensesPeriodKey,
    emailReceiveKey,
    emailSuccessKey,
    assignMoreKey,
    mainMessages
} from '../../assets/text/MultilingualText.js';

// context to store popover reference, when to show, and which popover to show
export const PopoverContext = createContext({
    onMouseOver: () => null,
});

const HostPanelBars = (props) => {
    const {
        fsaID,
        fsaDetails,
        groupByHosts,
        setGroupByHosts,
        isLoading,
        setIsLoading,
        showEmailSuccess,
        setShowEmailSuccess,
        rowRender
    } = props;
    const {
        siteLanguageDefault,
        accessToken
    } = useContext(UserContext);
    const localization = useLocalization();
    const history = useHistory();

    const [items, setItems] = useState(groupByHosts);

    const [checkedHosts, setCheckedHosts] = useState([]);
    const [checkedDownloadableHosts, setCheckedDownloadableHosts] = useState([]);

    const [showPopover, setShowPopover] = useState(false);
    const [popoverType, setPopoverType] = useState('special');
    const [popoverRef, setPopoverRef] = useState();

    const [showEmailModal, setShowEmailModal] = useState(false);

    // closes the success email alert
    const alertEmailSuccessHandler = () => {
        setShowEmailSuccess(false);
    }

    // sets which popover to show and it's corresponding reference
    const handlePopoverMouseOver = useCallback((event) => {
        if (event.show) {
            setShowPopover(true);
        } else {
            setShowPopover(false);
        }

        if (event.banked) {
            setPopoverType('banked');
        } else if (event.special) {
            setPopoverType('special');
        } else if (event.cloud) {
            setPopoverType('cloud');
        }

        setPopoverRef(event.popoverRef);
    }, [setShowPopover, setPopoverType, setPopoverRef]);

    // handles panel bar expanding based on whether icons have been clicked or not
    const iconClicked = useRef(false);
    const handleSelect = (event) => {
        let name = event.target.props.children[0].props.children.props.data[0].name;
        let hostIndex = items.findIndex(host => host.name === name);
        if (!iconClicked.current) {
            if (items[hostIndex].expanded.length === 0) {
                items[hostIndex].expanded = ['.0'];
            } else {
                items[hostIndex].expanded = [];
            }
        } else {
            iconClicked.current = false;
        }
        setItems([...items]);
    };

    // excel export
    const formatExcelDate = (date) => {
        let dateSplit = new Date(date).toLocaleDateString('en-us', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        }).replace(",", "").split(" ");
        if (dateSplit[1].length === 1) {
            dateSplit[1] = "0" + dateSplit[1];
        }
        return dateSplit[1] + "/" + dateSplit[0] + "/" + dateSplit[2]
    }

    // update items based on check box clicked and show expired
    useEffect(() => {
        let newItems = JSON.parse(JSON.stringify(groupByHosts));

        newItems.forEach(item => {
            let hostIndex = groupByHosts.findIndex(host => host.name === item.name);
            groupByHosts[hostIndex].icons = item.icons;
            setGroupByHosts(groupByHosts);
        })

        setItems(JSON.parse(JSON.stringify(groupByHosts)));
    }, [groupByHosts]) // eslint-disable-line react-hooks/exhaustive-deps


    useEffect(() => {
        let newCheckedHosts = [];
        let newCheckedDownloadableHosts = [];

        items.forEach(item => {
            if (item.icons.checked) {
                newCheckedHosts.push(item);
            }
            if (item.icons.checked && item.icons.download) {
                item.products.forEach(product => {
                    if (product.candownload === 'YES') {
                        newCheckedDownloadableHosts.push(product.cart_id.toString());
                    }
                })
            }
        })

        setCheckedHosts(newCheckedHosts);
        setCheckedDownloadableHosts(newCheckedDownloadableHosts);
    }, [items]);

    const exportExcel = () => {
        let headerData = [{
            "Company Name": fsaDetails['customer_name'],
            "Address": fsaDetails['customer_address'],
            "City": fsaDetails['customer_city'],
            "State": fsaDetails['customer_state'],
            "Zip Code": fsaDetails['customer_zip_code'],
            "Country": fsaDetails['customer_country'],
            "Customer Email": fsaDetails['customer_email'],
            "Start Date": formatExcelDate(fsaDetails['start_date']),
            "End Date": formatExcelDate(fsaDetails['end_date']),
            "Currency Code": fsaDetails['currency_code']
        }];

        let periodData = [];
        let uniqueSubYears = new Set();
        let subPoolYears = {};


        uniqueSubYears = Array.from(uniqueSubYears).sort((a, b) => a - b);
        for (const year of uniqueSubYears) {
            headerData[0]['Subs Pool Year ' + year] = formatCurrency(subPoolYears[year], fsaDetails['locale'], fsaDetails['currency_code']);
        }

        let productData = []
        checkedHosts.forEach(host => {
            host.products.forEach(product => {
                productData.push({
                    'Host ID': product.nameExcel,
                    'Product': product.product_number,
                    'Description': product.description,
                    'Qty': product.quantity,
                    'Start Date': formatExcelDate(product.start_date),
                    'End Date': formatExcelDate(product.end_date),
                    'License Type': capitalizeSentence(product.license_type),
                    'Cost Per Unit': product.price,
                    'Total Cost': product.total_cost,
                    'Requested By': product.requested_by
                });
            });
        });

        let currentDate = new Date();

        const fileName = 'KSM_FSA_DETAILS_'
            + currentDate.getFullYear().toString()
            + (currentDate.getMonth() + 1).toString()
            + currentDate.getDate().toString();
        const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const fileExtension = '.xlsx';
        let worksheet = XLSX.utils.json_to_sheet(headerData);
        XLSX.utils.sheet_add_json(worksheet, periodData, {origin: "A4"});
        XLSX.utils.sheet_add_json(worksheet, productData, {origin: "A" + (headerData.length + 2 + periodData.length + 2 + 1).toString()});
        const wb = {Sheets: {'data': worksheet}, SheetNames: ['data']};
        const excelBuffer = XLSX.write(wb, {bookType: 'xlsx', type: 'array'});
        const data = new Blob([excelBuffer], {type: fileType});
        FileSaver.saveAs(data, fileName + fileExtension);
    }

    return (
        <>
            <PopoverContext.Provider value={{onMouseOver: handlePopoverMouseOver}}>
                {items.map((item, index) => {
                    return (
                        <PanelBar
                            key={index}
                            isControlled={true}
                            expanded={item.expanded}
                            onSelect={handleSelect}
                            className={'ksm-panelbar-default'}
                        >
                            <PanelBarItem
                                title={<HostPanelBarHeader
                                    items={items}
                                    index={index}
                                    setItems={setItems}
                                    setIsLoading={setIsLoading}
                                    iconClicked={iconClicked}
                                />}
                            >
                                <Grid
                                    className={'group-host-panelbar-grid'}
                                    data={orderBy(item.products, [{
                                        field: "product_number",
                                        dir: "desc"
                                    }])}
                                    rowRender={rowRender}
                                    scrollable={'none'}
                                    sortable={false}
                                >
                                    <GridColumn
                                        field="product_number"
                                        title={localization.toLanguageString(productNumberKey, mainMessages[siteLanguageDefault][productNumberKey])}
                                        cell={NoWrapCell}
                                    />
                                    <GridColumn
                                        field="description"
                                        title={localization.toLanguageString(descriptionKey, mainMessages[siteLanguageDefault][descriptionKey])}
                                    />
                                    <GridColumn
                                        field="quantity"
                                        title={localization.toLanguageString(qtyKey, mainMessages[siteLanguageDefault][qtyKey])}
                                        cell={TextAlignMiddleCell}
                                    />
                                    <GridColumn
                                        field="start_date"
                                        title={localization.toLanguageString(startDateKey, mainMessages[siteLanguageDefault][startDateKey])}
                                        cell={DateCell}
                                    />
                                    <GridColumn
                                        field="end_date"
                                        title={localization.toLanguageString(endDateKey, mainMessages[siteLanguageDefault][endDateKey])}
                                        cell={DateCell}
                                    />
                                    <GridColumn
                                        field="license_type"
                                        title={localization.toLanguageString(licenseTypeKey, mainMessages[siteLanguageDefault][licenseTypeKey])}
                                        cell={TextCapitalizeCell}
                                    />
                                    <GridColumn
                                        field="price"
                                        title={localization.toLanguageString(costPerUnitKey, mainMessages[siteLanguageDefault][costPerUnitKey])}
                                        cell={CurrencyCell}
                                    />
                                    <GridColumn
                                        field="total_cost"
                                        title={localization.toLanguageString(totalCostKey, mainMessages[siteLanguageDefault][totalCostKey])}
                                        cell={CurrencyCell}
                                    />
                                </Grid>
                            </PanelBarItem>
                        </PanelBar>
                    )
                })}
            </PopoverContext.Provider>

            <HostDetailPopover
                type={popoverType}
                showPopover={true}
                popoverRef={popoverRef}
            />

            {items.length === 0 && !isLoading && (
                <>
                    <b style={{color: "var(--color-alert-danger-base)"}}>
                        {localization.toLanguageString(noLicensesPeriodKey, mainMessages[siteLanguageDefault][noLicensesPeriodKey])}
                    </b>
                    <br/>
                    <br/>
                </>
            )}

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: 'row-reverse',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        gap: '0.5rem',
                    }}
                >
                    <div>
                        <Tooltip
                            anchorElement="target"
                            showCallout={true}
                            parentTitle={true}
                            openDelay={0}
                            position="left"
                        >
                            <Button
                                title={localization.toLanguageString(exportToExcelKey, mainMessages[siteLanguageDefault][exportToExcelKey])}
                                themeColor={"primary"}
                                size={"large"}
                                fillMode={"solid"}
                                onClick={exportExcel}
                                svgIcon={fileExcelIcon}
                            />
                        </Tooltip>
                    </div>

                    {fsaDetails?.status?.toLowerCase() !== 'expired' && items.length !== 0 && (
                        <>
                            <div>
                                <Tooltip
                                    anchorElement="target"
                                    showCallout={true}
                                    parentTitle={true}
                                    openDelay={0}
                                    position="left"
                                >
                                    <IconButton
                                        title={localization.toLanguageString(emailSelectedLicensesKey, mainMessages[siteLanguageDefault][emailSelectedLicensesKey])}
                                        themeColor={"primary"}
                                        fillMode={"solid"}
                                        size={"large"}
                                        disabled={checkedDownloadableHosts.length === 0}
                                        onClick={() => {
                                            setShowEmailModal(true);
                                        }}
                                        Icon={MailIcon}
                                    />
                                </Tooltip>
                            </div>

                            <div>
                                <Tooltip
                                    anchorElement="target"
                                    showCallout={true}
                                    parentTitle={true}
                                    openDelay={0}
                                    position="left"
                                >
                                    <IconButton
                                        title={localization.toLanguageString(downloadSelectedLicensesKey, mainMessages[siteLanguageDefault][downloadSelectedLicensesKey])}
                                        themeColor={"primary"}
                                        fillMode={"solid"}
                                        size={"large"}
                                        disabled={checkedDownloadableHosts.length === 0}
                                        onClick={() => {
                                            downloadLicenses(checkedDownloadableHosts, accessToken, setIsLoading, null, "GET_LIC_VIEW_FSA");
                                        }}
                                        Icon={DownloadIcon}
                                    />
                                </Tooltip>
                            </div>
                        </>
                    )}
                </div>

                {fsaDetails?.status?.toLowerCase() !== 'expired' && (
                    <Button
                        themeColor={"primary"}
                        size={"large"}
                        fillMode={"solid"}
                        onClick={() => {
                            history.push('/fsa-request-license?fsa_id=' + fsaID);
                        }}
                    >
                        {localization.toLanguageString(assignMoreKey, mainMessages[siteLanguageDefault][assignMoreKey])}
                    </Button>
                )}
            </div>

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '0.938rem'
                }}
            >
                {showEmailSuccess && (
                    <Alert
                        type={'success'}
                        showHandler={alertEmailSuccessHandler}
                        title={localization.toLanguageString(emailSuccessKey, mainMessages[siteLanguageDefault][emailSuccessKey])}
                        message={localization.toLanguageString(emailReceiveKey, mainMessages[siteLanguageDefault][emailReceiveKey])}
                    />
                )}
            </div>

            {showEmailModal && (
                <EmailDialog
                    setShowEmailModal={setShowEmailModal}
                    setShowEmailSuccess={setShowEmailSuccess}
                    transactionIDs={checkedDownloadableHosts}
                    setIsLoading={setIsLoading}
                    request_source='GET_LIC_VIEW_FSA'
                />
            )}
        </>
    )
}

export default HostPanelBars;